/* eslint-disable no-console */
import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  ReactNode,
} from 'react';
import { Incident } from '../../types/types';

interface CustomerServiceState {
  currentTab: number;
  incident: Incident;
  disable: boolean;
  InitialTktInfo: Incident;
}

interface CustomerServiceDispatch {
  setCurrentTab: (tabIndex: number) => void;
  updateIncident: (data: Partial<Incident>) => void;
  updateIncidentSection: <K extends keyof Incident>(
    section: K,
    data: Partial<Incident[K]>
  ) => void;
  setDisabled: (data: boolean) => void;
  updateInitialTktInfo: (data: Partial<Incident>) => void;
}

const CustomerServiceStateContext = createContext<CustomerServiceState>(
  {} as CustomerServiceState
);
const CustomerServiceDispatchContext = createContext<CustomerServiceDispatch>(
  {} as CustomerServiceDispatch
);

export const useCustomerServiceState = () =>
  useContext(CustomerServiceStateContext);
export const useCustomerServiceDispatch = () =>
  useContext(CustomerServiceDispatchContext);

export const defaultIncident: Incident = {
  basicInfo: {
    ticketId: '',
    ticketNumber: '',
    channel: '',
    request_type: '',
    category: '',
    firstName: '',
    sub_category: '',
    lastName: '',
    action_type: '',
    callback: '',
    agreementnumber: '',
    alterphonenumb: '',
    storenumber: '',
    shortDescription: '',
    description: '',
    language: '',
    attachments: [],
    status: '',
  },
  assignment: {
    assignmentGroup: '',
    esName: '',
    escalationContact: '',
    role: '',
    escalatedTo: '',
  },
  resolution: {
    resolved: false,
    rscode: '',
    rsnote: '',
  },
  activity: {
    activities: [],
    ticketNotes: [],
    businessDuration: '00:00:00',
  },
};

const CustomerServiceProvider = ({ children }: { children: ReactNode }) => {
  const [currentTab, setCurrentTab] = useState(0);
  const [incident, setIncident] = useState<Incident>(defaultIncident);
  const [InitialTktInfo, setInitialTktInfo] =
    useState<Incident>(defaultIncident);
  const [disable, setDisabled] = useState<boolean>(false);

  const updateIncident = (data: Partial<Incident>) => {
    setIncident((prev) => ({ ...prev, ...data }));
  };

  const updateIncidentSection = <K extends keyof Incident>(
    section: K,
    data: Partial<Incident[K]>
  ) => {
    setIncident((prev) => {
      const currentSection = prev[section];
      if (typeof currentSection === 'object' && currentSection !== null) {
        return {
          ...prev,
          [section]: {
            ...currentSection,
            ...data,
          },
        };
      }

      return {
        ...prev,
        [section]: data as Incident[K],
      };
    });
  };

  const updateInitialTktInfo = (data: Partial<Incident>) => {
    setInitialTktInfo((prev) => ({ ...prev, ...data }));
  };

  const stateValue = useMemo(
    () => ({
      currentTab,
      incident,
      disable,
      InitialTktInfo,
    }),
    [currentTab, incident, disable, InitialTktInfo]
  );

  const dispatchValue = useMemo(
    () => ({
      setCurrentTab,
      updateIncident,
      updateIncidentSection,
      setDisabled,
      updateInitialTktInfo,
    }),
    []
  );

  return (
    <CustomerServiceStateContext.Provider value={stateValue}>
      <CustomerServiceDispatchContext.Provider value={dispatchValue}>
        {children}
      </CustomerServiceDispatchContext.Provider>
    </CustomerServiceStateContext.Provider>
  );
};

export default CustomerServiceProvider;
